<script setup lang="ts">
import { computed, ref, useSlots, watch } from 'vue'
import { VIcon } from '@/modules/shared/components'
import VSkeletonBar from '@/modules/shared/components/v-skeleton-bar.vue'
import { ChevronDown } from '@/modules/shared/components/icons'
import { format as vtableFormat } from '@/modules/shared/utils/v-table'
import { formatCurrency } from '@/modules/shared/utils/currency'

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface Column {
  key: string
  label: string
  type: 'boolean' | 'currency' | 'date' | 'integer' | 'multiple' | 'number' | 'percent' | 'string'
  // Value resolution per render context. Each slot accepts:
  //   - a function: called with the appropriate input, its result is used
  //   - null:       cell stays present (for grid alignment) but its content is
  //                 hidden — slot templates for this column do NOT fire in
  //                 that mode, and the default render is suppressed too.
  //   - undefined:  falls through (aggregate → flatten → item[key];
  //                 subrow → flatten's behavior)
  // Subrow inherits flatten's null-hides behavior unless it explicitly opts in
  // with its own function. Visibility (null-hides) takes precedence over slot
  // templates — column config decides whether a cell renders at all; the
  // slot decides the cell's content when it does render.
  aggregate?: ((items: any[]) => any) | null
  flatten?: ((item: any) => any) | null
  subrow?: ((item: any) => any) | null | false
  align?: 'left' | 'center' | 'right'
  hidden?: boolean
}

interface SortRule {
  key: string
  direction?: 'asc' | 'desc'
}

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

const props = withDefaults(
  defineProps<{
    columns: Column[]
    items: any[]
    groupBy?: string | null
    limit?: number
    emptyTitle?: string | null
    emptyMessage?: string | null
    loading?: boolean
    skeletonRows?: number
    sortBy?: SortRule[]
    fallbackSortBy?: SortRule[]
    canExpand?: (items: any[]) => boolean
  }>(),
  {
    sortBy: () => [],
    fallbackSortBy: () => [],
    emptyTitle: null,
    emptyMessage: null,
    loading: false,
    skeletonRows: 5,
  },
)

const activeLimit = ref(props.limit)
const hasGrouping = computed(() => Boolean(props.groupBy))
const slots = useSlots()

// -----------------------------------------------------------------------------
// Columns
// -----------------------------------------------------------------------------

const alignments = {
  // align prop
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',

  // type prop
  boolean: 'text-center',
  currency: 'text-right',
  date: 'text-center',
  integer: 'text-right',
  multiple: 'text-center',
  number: 'text-right',
  percent: 'text-right',
  string: 'text-center',
}

const resolvedColumns = computed(() =>
  [...props.columns].map((col) => ({
    ...col,
    align: alignments[col.align ?? col.type],
  })),
)

const visibleColumns = computed(() => resolvedColumns.value.filter((col) => !col.hidden))

// -----------------------------------------------------------------------------
// Accent colors
// -----------------------------------------------------------------------------

const palette: Record<string, string> = {
  V_CAPITAL: '#3b88af',
  V_MANAGEMENT_FEE: '#7d8ad0',
  V_OTHER_FEE: '#7d8ad0',
  V_COMMITMENT: '#D2D5DB',
  V_DISTRIBUTION_RETURN_OF_CAPITAL: '#408F93',
  V_DISTRIBUTION_PREFERRED_RETURN: '#4FB8A9',
  V_DISTRIBUTION_PROFIT: '#46B37D',
  V_DISTRIBUTION_CARRIED_INTEREST: '#7d8ad0',
  V_DISTRIBUTION_FEE: '#7d8ad0',
  V_UNPAID: '#d97706',
  V_PAID: '#408F93',
}

const accentColor = (token?: string) => {
  if (token?.startsWith('#')) return token
  return palette[token ?? ''] ?? '#6b7280'
}

// -----------------------------------------------------------------------------
// Formatters
// -----------------------------------------------------------------------------

const formatters: Record<string, (value: any, items?: any[]) => any> = {
  date: (v) => vtableFormat(v, 'date'),
  currency: (v) => formatCurrency(v.amount, v.currency),
  percent: (v) => `${(v * 100).toFixed(2)}%`,
  number: (v) => v,
  integer: (v) => Math.round(v),
  boolean: (v) => (v ? 'Yes' : 'No'),
  string: (v) => v ?? '',
}

const formatValue = (raw: any, column: Column) => {
  if (column.type === 'currency') {
    return formatCurrency(raw, { currency: 'USD', decimals: 2 })
  }

  if (column.type === 'date') {
    return vtableFormat(raw, 'date')
  }

  return formatters[column.type]?.(raw) ?? raw
}

// -----------------------------------------------------------------------------
// Per-context value resolution
// -----------------------------------------------------------------------------
// Each slot supports three states:
//   - function: called and its result used
//   - null:     content hidden (cell <div> is still rendered for grid alignment)
//   - undefined / omitted: falls through to the next-most-relevant resolver
//
// Subrow inherits flatten's behavior when undefined — including its "null hides"
// state — so declaring flatten: null on a column also hides subrow cells unless
// subrow explicitly opts in with its own function.

const isHeaderHidden = (column: Column) => column.aggregate === null
const isFlatHidden = (column: Column) => column.flatten === null
const isSubrowHidden = (column: Column) =>
  column.subrow === null || column.subrow === false || (column.subrow === undefined && isFlatHidden(column))

const resolveHeaderValue = (column: Column, items: any[]) => {
  if (typeof column.aggregate === 'function') return column.aggregate(items)
  if (typeof column.flatten === 'function') return column.flatten(items[0])
  return items[0]?.[column.key]
}

const resolveFlatValue = (column: Column, item: any) => {
  if (typeof column.flatten === 'function') return column.flatten(item)
  return item?.[column.key]
}

const resolveSubrowValue = (column: Column, item: any) => {
  if (typeof column.subrow === 'function') return column.subrow(item)
  return resolveFlatValue(column, item)
}

// -----------------------------------------------------------------------------
// Expanding/collapsing logic
// -----------------------------------------------------------------------------

const expanded = ref<Set<string>>(new Set())

const toggleAll = () => {
  const allExpanded = groupedItems.value.every((group) => expanded.value.has(group.groupId))
  if (allExpanded) {
    expanded.value.clear()
    return
  }

  for (const group of groupedItems.value) {
    expanded.value.add(group.groupId)
  }
}

function toggleGroup(groupId: string) {
  if (expanded.value.has(groupId)) {
    expanded.value.delete(groupId)
    return
  }

  expanded.value.add(groupId)
}

// -----------------------------------------------------------------------------
// Limit items
// -----------------------------------------------------------------------------

const isLimited = computed(() => {
  if (activeLimit.value == null) return false
  const total = hasGrouping.value ? formattedGroups.value.length : formattedItems.value.length
  return total > activeLimit.value
})

const loadAll = () => {
  if (activeLimit.value == null) return
  activeLimit.value = hasGrouping.value ? formattedGroups.value.length : formattedItems.value.length
}

// -----------------------------------------------------------------------------
// Sorting
// -----------------------------------------------------------------------------

const normalizeSortRules = (rules?: SortRule[] | null) => {
  return (rules ?? [])
    .filter((rule): rule is SortRule => Boolean(rule?.key))
    .map((rule) => ({
      key: rule.key,
      direction: rule.direction ?? 'asc',
    }))
}

const propSortRules = computed(() => normalizeSortRules(props.sortBy))
const fallbackSortRules = computed(() => normalizeSortRules(props.fallbackSortBy))

const activeSorts = ref<Required<SortRule>[]>(propSortRules.value)

// merge active sorts with fallback prop sorts, keeping order and dropping dup keys
const mergeSortRules = (...ruleSets: Required<SortRule>[][]) => {
  const columnKeys = new Set(props.columns.map((col) => col.key))
  const seen = new Set<string>()
  const merged: Required<SortRule>[] = []

  for (const rules of ruleSets) {
    for (const rule of rules) {
      if (!rule.key || seen.has(rule.key) || !columnKeys.has(rule.key)) continue
      merged.push(rule)
      seen.add(rule.key)
    }
  }

  return merged
}

watch(
  propSortRules,
  (next) => {
    activeSorts.value = next
  },
  { deep: true },
)

const resolvedSorts = computed(() => mergeSortRules(activeSorts.value, fallbackSortRules.value))

const primarySort = computed(() => resolvedSorts.value[0])
const activeSortKey = computed(() => primarySort.value?.key ?? null)
const activeSortDir = computed<'asc' | 'desc'>(() => primarySort.value?.direction ?? 'asc')

const sort = (key: string) => {
  const currentPrimary = resolvedSorts.value[0]
  const nextDirection =
    currentPrimary?.key === key && currentPrimary.direction === 'asc' ? ('desc' as const) : ('asc' as const)

  activeSorts.value = [{ key, direction: nextDirection }]
}

const getColumn = (key: string) => props.columns.find((col) => col.key === key)

const getComparableValue = (val: any, column?: Column) => {
  if (val == null) return null

  switch (column?.type) {
    case 'currency':
    case 'integer':
    case 'number':
    case 'percent':
      if (typeof val === 'object' && val !== null && 'amount' in val) return Number((val as any).amount)
      return Number(val)
    case 'date': {
      const [p] = String(val).split(/[T ]/)
      const [y, m, d] = p.split('-')
      return new Date(Number(y), Number(m) - 1, Number(d)).getTime()
    }
    case 'boolean':
      return Boolean(val)
    default:
      return String(val).toLowerCase()
  }
}

const compareWithSorts = <T,>(
  a: T,
  b: T,
  getter: (item: T, key: string) => any = (item: any, key: string) => item[key],
) => {
  for (const rule of resolvedSorts.value) {
    const column = getColumn(rule.key)
    const A = getter(a, rule.key)
    const B = getter(b, rule.key)

    const aComparable = getComparableValue(A, column)
    const bComparable = getComparableValue(B, column)

    if (aComparable == null && bComparable == null) continue
    if (aComparable == null) return rule.direction === 'asc' ? -1 : 1
    if (bComparable == null) return rule.direction === 'asc' ? 1 : -1
    if (aComparable < bComparable) return rule.direction === 'asc' ? -1 : 1
    if (aComparable > bComparable) return rule.direction === 'asc' ? 1 : -1
  }

  return 0
}

// -----------------------------------------------------------------------------
// Data transformations
// -----------------------------------------------------------------------------

// 1. Sort items
const sortedItems = computed(() => {
  if (!resolvedSorts.value.length) return props.items
  return [...props.items].sort((a, b) => compareWithSorts(a, b))
})

// For each row, pre-compute the resolved raw and the column-type-formatted
// value per column for both flat and subrow modes. Templates and slots read
// directly from __flat / __subrow instead of recomputing per render.
const formattedItems = computed(() =>
  sortedItems.value.map((item) => {
    const formatted: Record<string, any> = {
      __raw: item,
      __key: item.id ?? item[props.columns[0].key],
      __accent: accentColor(item.accent_color),
      __flat: {} as Record<string, { value: any; formatted: any }>,
      __subrow: {} as Record<string, { value: any; formatted: any }>,
    }
    for (const column of props.columns) {
      const flatValue = resolveFlatValue(column, item)
      const subrowValue = resolveSubrowValue(column, item)
      formatted.__flat[column.key] = { value: flatValue, formatted: formatValue(flatValue, column) }
      formatted.__subrow[column.key] = { value: subrowValue, formatted: formatValue(subrowValue, column) }
    }

    return formatted
  }),
)

// 2. Group items

const groupedItems = computed(() => {
  if (!props.groupBy) return []

  const groups: Record<string, { items: any[] }> = {}

  for (const item of sortedItems.value) {
    const key = item[props.groupBy]
    if (key == null) continue

    if (!groups[key]) {
      groups[key] = { items: [] }
    }

    groups[key].items.push(item)
  }

  return Object.entries(groups).map(([groupId, group]) => ({
    groupId,
    items: group.items,
  }))
})

// 3. Compute aggregate values

const aggregatedGroups = computed(() => {
  return groupedItems.value.map((group) => {
    const rawAggregates: Record<string, any> = {}

    for (const column of props.columns) {
      const items = group.items
      rawAggregates[column.key] = resolveHeaderValue(column, items)
    }

    return {
      ...group,
      rawAggregates,
    }
  })
})

// 4. Sort groups

const sortedGroups = computed(() => {
  if (!resolvedSorts.value.length) return aggregatedGroups.value
  return [...aggregatedGroups.value].sort((a, b) => compareWithSorts(a.rawAggregates, b.rawAggregates))
})

// 5. Format values

const formattedGroups = computed(() => {
  return sortedGroups.value.map((group) => {
    const groupAccent = accentColor(group.items[0]?.accent_color)
    const aggregated: Record<string, any> = {}
    const formattedItems = group.items.map((item) => {
      const obj: Record<string, any> = {
        __raw: item,
        __key: item.id ?? item[props.columns[0].key],
        __accent: groupAccent,
        __flat: {} as Record<string, { value: any; formatted: any }>,
        __subrow: {} as Record<string, { value: any; formatted: any }>,
      }
      for (const column of props.columns) {
        const flatValue = resolveFlatValue(column, item)
        const subrowValue = resolveSubrowValue(column, item)
        obj.__flat[column.key] = { value: flatValue, formatted: formatValue(flatValue, column) }
        obj.__subrow[column.key] = { value: subrowValue, formatted: formatValue(subrowValue, column) }
      }
      return obj
    })

    for (const column of props.columns) {
      aggregated[column.key] = formatValue(group.rawAggregates[column.key], column)
    }

    return {
      ...group,
      __accent: groupAccent,
      formattedItems,
      aggregated,
    }
  })
})

// 6. Limit groups

const visibleGroups = computed(() => {
  if (activeLimit.value == null) return formattedGroups.value
  return formattedGroups.value.slice(0, activeLimit.value)
})

const visibleItems = computed(() => {
  if (activeLimit.value == null) return formattedItems.value
  return formattedItems.value.slice(0, activeLimit.value)
})

const hasVisibleRows = computed(() =>
  hasGrouping.value ? visibleGroups.value.length > 0 : visibleItems.value.length > 0,
)
const resolvedEmptyTitle = computed(() => props.emptyTitle ?? 'No data to display')
const resolvedEmptyMessage = computed(
  () => props.emptyMessage ?? 'Try adjusting your filters or add data to see results here.',
)
const skeletonRowCount = computed(() => Math.max(1, props.skeletonRows ?? 5))
const skeletonVisibleColumns = computed(() =>
  visibleColumns.value.length ? visibleColumns.value : resolvedColumns.value,
)

const COL_MIN_WIDTH = 150

const hasActions = computed(() => !!slots.actions)

const gridColumnCount = computed(() => visibleColumns.value.length + (hasActions.value ? 1 : 0))

const gridColumns = computed(() => {
  return `2rem repeat(${gridColumnCount.value}, minmax(max-content, 1fr))`
})

const gridMinWidth = computed(() => {
  return `calc(2rem + ${gridColumnCount.value} * ${COL_MIN_WIDTH}px)`
})
</script>

<template>
  <div class="vtable" :class="{ 'vtable--grouped': hasGrouping }">
    <div class="vtable__inner" :style="{ gridTemplateColumns: gridColumns, minWidth: gridMinWidth }">
      <div class="vtable__header" :class="{ expanded: hasGrouping && expanded.size >= visibleGroups.length }">
        <div class="vtable__caret_container">
          <button v-if="hasGrouping" @click="toggleAll">
            <ChevronDown class="vtable__caret h-4 w-4" />
          </button>
        </div>
        <div
          v-for="column in visibleColumns"
          :key="column.key"
          @click="sort(column.key)"
          class="vtable__header_cell"
          :class="column.align"
        >
          <span>
            {{ column.label }}
          </span>
          <span v-if="activeSortKey === column.key">
            {{ activeSortDir === 'asc' ? '▲' : '▼' }}
          </span>
        </div>
        <div v-if="$slots.actions" class="vtable__header_cell"></div>
      </div>
      <div class="vtable__body">
        <template v-if="loading">
          <div v-for="row in skeletonRowCount" :key="`skeleton-${row}`" class="vtable__row">
            <div class="vtable__accent vtable__accent--skeleton"></div>
            <div class="vtable__caret_container vtable__caret_cell"></div>

            <div
              v-for="column in skeletonVisibleColumns"
              :key="`skeleton-cell-${column.key}-${row}`"
              class="vtable__cell"
              :class="column.align"
            >
              <VSkeletonBar :min="64" :max="140" />
            </div>
          </div>
        </template>
        <template v-else-if="hasVisibleRows">
          <template v-if="hasGrouping">
            <div v-for="(group, index) in visibleGroups" :key="group.groupId" class="vtable__group_row">
              <div class="vtable__accent vtable__accent--group" :style="{ backgroundColor: group.__accent }"></div>
              <div class="vtable__group_header" :class="{ expanded: expanded.has(group.groupId) }">
                <div class="vtable__caret_container">
                  <button
                    v-if="hasGrouping && (!canExpand || canExpand(group.items))"
                    @click="toggleGroup(group.groupId)"
                  >
                    <ChevronDown class="vtable__caret h-4 w-4" />
                  </button>
                </div>

                <div v-for="column in visibleColumns" :key="column.key" class="vtable__cell" :class="column.align">
                  <template v-if="isHeaderHidden(column)"></template>
                  <template v-else-if="slots[column.key]">
                    <slot
                      :name="column.key"
                      :value="group.rawAggregates[column.key]"
                      :formatted="group.aggregated[column.key]"
                      :column="column"
                      :item="group.rawAggregates"
                      :group="group"
                      :mode="'aggregate'"
                    />
                  </template>
                  <template v-else>
                    {{ group.aggregated[column.key] }}
                  </template>
                </div>
                <div v-if="$slots.actions" class="vtable__cell">
                  <slot name="actions" :item="group.rawAggregates" :group="group" :mode="'aggregate'" />
                </div>
              </div>

              <div v-if="expanded.has(group.groupId)" class="vtable__expanded">
                <div v-for="item in group.formattedItems" :key="item.__key" class="vtable__row">
                  <div class="vtable__caret_container"></div>

                  <div v-for="column in visibleColumns" :key="column.key" class="vtable__cell" :class="column.align">
                    <template v-if="isSubrowHidden(column)"></template>
                    <template v-else-if="slots[column.key]">
                      <slot
                        :name="column.key"
                        :value="item.__subrow[column.key].value"
                        :formatted="item.__subrow[column.key].formatted"
                        :column="column"
                        :item="item.__raw"
                        :group="group"
                        :mode="'subrow'"
                      />
                    </template>
                    <template v-else>
                      {{ item.__subrow[column.key].formatted }}
                    </template>
                  </div>
                  <div v-if="$slots.actions" class="vtable__cell">
                    <slot name="actions" :item="item.__raw" :group="group" :mode="'subrow'" />
                  </div>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <div v-for="item in visibleItems" :key="item.__key" class="vtable__row">
              <div class="vtable__accent" :style="{ backgroundColor: item.__accent }"></div>
              <div class="vtable__caret_container vtable__caret_cell"></div>

              <div v-for="column in visibleColumns" :key="column.key" class="vtable__cell" :class="column.align">
                <template v-if="isFlatHidden(column)"></template>
                <template v-else-if="slots[column.key]">
                  <slot
                    :name="column.key"
                    :value="item.__flat[column.key].value"
                    :formatted="item.__flat[column.key].formatted"
                    :column="column"
                    :item="item.__raw"
                    :mode="'flatten'"
                  />
                </template>
                <template v-else>
                  {{ item.__flat[column.key].formatted }}
                </template>
              </div>
              <div v-if="$slots.actions" class="vtable__cell">
                <slot name="actions" :item="item.__raw" :mode="'flatten'" />
              </div>
            </div>
          </template>
        </template>
        <div v-else class="vtable__empty">
          <div class="vtable__empty_icon">
            <VIcon name="SlashCircle01" class="h-5 w-5" />
          </div>
          <div class="vtable__empty_title">{{ resolvedEmptyTitle }}</div>
          <div class="vtable__empty_text">
            {{ resolvedEmptyMessage }}
          </div>
        </div>
        <!-- <div
        v-if="isLimited"
        class="pointer-events-none absolute bottom-0 left-0 right-0 z-40 h-32 w-full bg-gradient-to-t from-white via-white/90 to-transparent"
      ></div> -->
      </div>
    </div>
  </div>
  <!-- <div v-if="isLimited" class="absolute z-50 -mt-[15px] flex justify-center">
    <VButton size="xs" @click="loadAll">View All</VButton>
  </div> -->
</template>

<style scoped>
.vtable {
  @apply w-full overflow-x-auto overflow-y-hidden;
  @apply rounded-md border-none bg-white shadow-sm ring-1 ring-gray-700/20;
}

.vtable__inner {
  display: grid;
}

.vtable__header {
  @apply sticky top-0 z-30;
  @apply rounded-t-[7px] border-b border-gray-200;
  @apply bg-white/50 bg-clip-padding backdrop-blur-md;
  @apply text-xs font-semibold uppercase text-gray-700;
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
}

.vtable__header_cell {
  @apply cursor-pointer select-none whitespace-nowrap;
  @apply gap-1 px-4 py-3;
}

.vtable__body {
  @apply relative;
  @apply text-sm text-gray-700;
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
}

.vtable__group_row {
  @apply relative;
  @apply border-b border-gray-100;
  @apply text-sm;
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
}

.vtable__group_row:last-child {
  @apply border-b-0;
}

.vtable__expanded {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
}

.vtable__group_header {
  @apply relative overflow-hidden;
  @apply bg-white/50 backdrop-blur-md hover:bg-gray-100/50;
  @apply sticky top-[41px] z-20 ml-[5px] items-center;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
}

.vtable__group_header.expanded .vtable__cell {
  @apply border-b border-gray-100;
}

.vtable__row:not(:last-child) .vtable__cell {
  @apply border-b border-gray-100;
}

.vtable__row {
  @apply relative;
  @apply bg-white hover:bg-gray-100/50;
  @apply ml-0;
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
}

.vtable--grouped .vtable__row {
  @apply ml-[5px];
}

.vtable__row .vtable__cell:first-child {
  @apply !pl-0;
}

.vtable__cell {
  @apply whitespace-nowrap px-4 py-3;
}

.vtable__accent {
  @apply pointer-events-none absolute left-0 z-20;
  @apply bottom-[1px] top-[1px];
  @apply box-border h-full w-1.5 rounded-sm border border-white;
}

.vtable__accent--group {
  @apply bottom-0 top-0;
}

.vtable__accent--skeleton {
  @apply bg-gray-200;
  background-color: #e5e7eb;
}

.vtable__caret_cell {
  @apply py-2;
}

.vtable__row:not(:last-child) .vtable__caret_cell {
  @apply border-b border-gray-200;
}

.vtable__header.expanded .vtable__caret,
.vtable__group_header.expanded .vtable__caret {
  @apply rotate-0;
}

.vtable__caret_container {
  @apply flex items-center justify-center;
}

.vtable__caret {
  @apply -rotate-90 transform text-gray-700;
}

.vtable__empty {
  @apply flex flex-col items-center justify-center gap-2 px-6 py-12 text-center;
  @apply text-gray-600;
  grid-column: 1 / -1;
}

.vtable__empty_icon {
  @apply flex h-11 w-11 items-center justify-center rounded-full bg-gray-50 text-gray-500 ring-1 ring-gray-200;
}

.vtable__empty_title {
  @apply text-base font-semibold text-gray-900;
}

.vtable__empty_text {
  @apply max-w-xl text-sm;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
